import { z } from 'zod';

/**
 * Contrato del plan de estudios completo.
 *
 * El programa (`/api/program`) enseña los 10 meses: EN QUÉ ORDEN se estudia y
 * CUÁNDO. El plan de estudios enseña el PLAN: los 13 niveles, 60 módulos y 6
 * proyectos que una persona recorre desde cero absoluto hasta el despliegue.
 *
 * Es la respuesta a "¿esto que vendo es de verdad o una promesa?" vista entera.
 * Igual que el programa, es pública, idéntica para todo el mundo y no lleva ni
 * nombre ni progreso. No puede ser cacheable con datos de la cuenta y sí tiene
 * que poder cachearse: aquí no hay nada de nadie.
 *
 * Cada módulo lleva un `estado` derivado en el backend:
 *
 *   · `completo` — los meses que lo entregan ya están escritos y son todos
 *     accesibles. Quien quiera aprenderlo, puede: hueco cubierto.
 *   · `parcial`  — parte del módulo ya se enseña en meses escritos, pero falta
 *     al menos un hueco. Se ve el mapa sin pintarlo todo de verde.
 *   · `proximo`  — todavía no se enseña en ningún mes escrito.
 *
 * El `estado` se calcula (no es un dato del archivo) por la misma razón que
 * `disponible` en el programa: un módulo completo cuyo mes no está escrito no
 * puede pintarse en verde, y eso lo decide el contenido al leerlo, no una nota
 * que alguien escriba a mano.
 */

export const planMesRespuestaSchema = z.object({
  month: z.number().int().min(1).max(12),
  /** Título real del mes en el programa, o "Mes N" si todavía no existe. */
  title: z.string().min(1),
  /** ¿Ese mes tiene lecciones escritas y accesibles? */
  disponible: z.boolean(),
});

export const planModuloRespuestaSchema = z.object({
  id: z.string().min(1),
  /** Número del módulo. `null` en los proyectos, que no se numeran. */
  numero: z.number().int().min(1).nullable(),
  titulo: z.string().min(1),
  tipo: z.enum(['modulo', 'proyecto']),
  estado: z.enum(['completo', 'parcial', 'proximo']),
  meses: z.array(planMesRespuestaSchema),
  temas: z.array(z.string().min(1)).min(1),
});

export const planNivelRespuestaSchema = z.object({
  nivel: z.number().int().min(0).max(12),
  titulo: z.string().min(1),
  modulos: z.array(planModuloRespuestaSchema).min(1),
});

export const planResponseSchema = z.object({
  meta: z.object({
    titulo: z.string().min(1),
    subtitulo: z.string().min(1),
    totalNiveles: z.number().int().min(1),
    totalModulos: z.number().int().min(0),
    totalProyectos: z.number().int().min(0),
    completos: z.number().int().min(0),
    parciales: z.number().int().min(0),
    proximos: z.number().int().min(0),
  }),
  niveles: z.array(planNivelRespuestaSchema).min(1),
});

/**
 * @typedef {import('zod').infer<typeof planModuloRespuestaSchema>} PlanModulo
 * @typedef {import('zod').infer<typeof planNivelRespuestaSchema>} PlanNivel
 * @typedef {import('zod').infer<typeof planResponseSchema>} PlanResponse
 */

// ─── Formato del archivo de contenido ────────────────────────────────────────

/**
 * `content/plan-de-estudios.js` también se valida, como el resto de `content/`.
 *
 * Es un schema aparte del de la respuesta porque el archivo y la respuesta no
 * son lo mismo: el archivo declara `meses` y `completo` como intención, y la
 * respuesta añade el `estado` que se deriva de los meses realmente escritos.
 */

const planModuloFileSchema = z.object({
  id: z.string().min(1),
  numero: z.number().int().min(1).optional(),
  titulo: z.string().min(1),
  tipo: z.enum(['modulo', 'proyecto']).default('modulo'),
  /**
   * Qué meses del programa entregan este módulo. Vacío si todavía no se enseña
   * en ningún sitio.
   */
  meses: z.array(z.number().int().min(1).max(12)).default([]),
  /** El equipo cree que los meses declarados ya cubren TODO el módulo. */
  completo: z.boolean(),
  temas: z.array(z.string().min(1)).min(1),
});

const planNivelFileSchema = z.object({
  nivel: z.number().int().min(0).max(12),
  titulo: z.string().min(1),
  modulos: z.array(planModuloFileSchema).min(1),
});

export const planDeEstudiosFileSchema = z.array(planNivelFileSchema).min(1);

/**
 * El plan, con todas las trampas típicas de un fichero de datos grande y
 * editado a mano.
 *
 * Ninguna de estas comprobaciones la hace Zod, porque una a una todas estas
 * redes son válidas:
 *
 *   · Un nivel repetido (0, 1, 1) aparece dos veces en el mapa y nadie se
 *     entera de si el plan tiene dos "nivel 1".
 *   · Dos módulos con el mismo `numero` rompen la numeración visible.
 *   · Un módulo número 5 y otro 7 (sin 6) hace que el contador "módulos 1..N"
 *     de la página no cuadre.
 *   · `completo: true` con `meses: []` se pintaría verde sin que lo entregue
 *     ningún mes: la promesa del archivo es que verde significa "enseñado".
 *
 * @param {import('zod').infer<typeof planDeEstudiosFileSchema>} plan
 */
export function planSinDuplicados(plan) {
  const niveles = new Set();
  const ids = new Set();
  const numeros = new Set();
  const numerosDeModulos = [];

  for (const nivel of plan) {
    if (niveles.has(nivel.nivel)) {
      throw new Error(`El plan define dos veces el nivel ${nivel.nivel}.`);
    }
    niveles.add(nivel.nivel);

    for (const modulo of nivel.modulos) {
      if (ids.has(modulo.id)) {
        throw new Error(`El plan define dos veces el módulo ${modulo.id}.`);
      }
      ids.add(modulo.id);

      if (modulo.tipo === 'proyecto') {
        if (modulo.numero !== undefined) {
          throw new Error(`El proyecto ${modulo.id} no debe llevar número de módulo.`);
        }
      } else {
        if (modulo.numero === undefined) {
          throw new Error(`El módulo ${modulo.id} necesita un número.`);
        }
        if (numeros.has(modulo.numero)) {
          throw new Error(`El plan define dos veces el módulo ${modulo.numero}.`);
        }
        numeros.add(modulo.numero);
        numerosDeModulos.push(modulo.numero);
      }

      if (modulo.completo && modulo.meses.length === 0) {
        throw new Error(`El módulo ${modulo.id} está marcado completo sin ningún mes que lo entregue.`);
      }

      if (new Set(modulo.meses).size !== modulo.meses.length) {
        throw new Error(`El módulo ${modulo.id} repite un mes: ${JSON.stringify(modulo.meses)}.`);
      }
    }
  }

  const enOrden = numerosDeModulos.sort((a, b) => a - b);
  const consecutivos = enOrden.every((numero, indice) => numero === indice + 1);

  if (!consecutivos) {
    throw new Error(
      `Los módulos deben numerarse de 1 a ${enOrden.length} sin saltos: ${JSON.stringify(enOrden)}.`,
    );
  }

  return plan;
}